"use client"

import { Button, IconButton } from "@/elements"
import { PostAJobModal } from "@/components/PostAJobModal"
import type { NovaSession } from "@/types"
import { JobsHeaderNav } from "./JobsHeaderNav"
import { PlusIcon } from "@heroicons/react/24/outline"
import { ampli } from "@/ampli"
import { CreativeWorkTypeFilter, LocationFilter, SkillsFilter, WorkTypeFilter } from "@/components/Filters"
import { ProductionRolesFilter } from "@/components/Filters/ProductionRolesFilter"
import React, { FC } from "react"

interface Props {
  serverSession: NovaSession
  activeApplicationsCount: number | undefined
  activeInvitationsCount: number | undefined
  hasSubscription: boolean
  onClickFilterWithoutSubscription: () => void
}

export const JobsHeader: FC<Props> = ({
  serverSession,
  activeApplicationsCount,
  activeInvitationsCount,
  hasSubscription,
  onClickFilterWithoutSubscription,
}) => {
  const [showPostAJobModal, setShowPostAJobModal] = React.useState(false)

  const onSelectFilter = (type: string, value: string) => {
    ampli.jobFilterApply({
      filter_type: type,
      filter_value: value,
    })
  }

  const onClickOverride = (filterType: "role" | "location" | "work-type" | "skill" | "payment-type") => {
    ampli.jobFilterClick({
      filter_type: filterType,
    })
    if (hasSubscription || filterType === "location") {
      return { hasSubscription: true }
    }
    onClickFilterWithoutSubscription()
    return { hasSubscription: false }
  }

  return (
    <>
      <div className="sticky top-[72px] z-30 border-b border-bg-3 bg-bg-1 pb-4 md:top-0">
        <div className="mx-auto w-full max-w-xl border-t border-bg-2 pt-6 sm:border-none sm:px-5 sm:pb-4 md:top-0 md:px-12">
          <div className="no-scrollbar flex w-full flex-row items-center justify-between overflow-x-scroll border-fg-3">
            <JobsHeaderNav
              activeApplicationsCount={activeApplicationsCount}
              activeInvitationsCount={activeInvitationsCount}
            />

            <Button className="hidden sm:flex" onClick={() => setShowPostAJobModal(true)}>
              Post a job
            </Button>
          </div>
        </div>
        <div className="mx-auto mt-4 max-w-xl sm:mt-0 sm:px-5 sm:pl-0 md:w-full md:px-12">
          <div className="no-scrollbar flex flex-1 flex-row gap-2 overflow-scroll px-5 sm:overflow-visible md:px-0">
            <ProductionRolesFilter
              onClickOverride={() => onClickOverride("role")}
              onSelect={(value: string) => {
                onSelectFilter("role", value)
              }}
            />
            <LocationFilter
              onClickOverride={() => onClickOverride("location")}
              onSelect={(value: string) => {
                onSelectFilter("location", value)
              }}
            />
            <CreativeWorkTypeFilter
              onClickOverride={() => onClickOverride("work-type")}
              onSelect={(value: string) => {
                onSelectFilter("workType", value)
              }}
            />
            <SkillsFilter
              onClickOverride={() => onClickOverride("skill")}
              menuPosition="right"
              onSelect={(value: string) => {
                onSelectFilter("skill", value)
              }}
            />
            <WorkTypeFilter
              onClickOverride={() => onClickOverride("payment-type")}
              onSelect={(value: string) => {
                onSelectFilter("paymentType", value)
              }}
            />
          </div>
        </div>
      </div>
      <div className="fixed bottom-6 right-5 z-20 sm:hidden">
        <IconButton Icon={PlusIcon} variant="primary" size="xl" floating onClick={() => setShowPostAJobModal(true)} />
      </div>
      <PostAJobModal
        open={showPostAJobModal}
        onClose={() => setShowPostAJobModal(false)}
        serverSession={serverSession}
        entryPoint="find-work"
      />
    </>
  )
}
